import { Alert, Grid, Heading, List, View } from "@instructure/ui";
import { t } from "i18next";
import React, { useContext } from "react";
import { useLocation } from "react-router";
import { Link, Outlet } from "react-router-dom";
import styled from "styled-components";
import { UserDetailContext } from "../utils/Context";

const SetupNavLink = styled(Link)`
  color: #0374b5;
  text-decoration: none;
  display: block;
  padding-left: 6px;
  padding: 5px 15px;
  &:hover {
    text-decoration: underline;
  }
  &[role="link"]:focus {
    border: 2px solid #27323b;
    border-radius: 5px;
    padding: 3px 13px;
    &.active {
      background-color: #fff;
      color: #27323b;
      border: 1px solid #27323b;
    }
`;

const Setup = () => {
  const location = useLocation();
  const userDetails = useContext(UserDetailContext);
  const { pathname } = location;

  const getNavStyle = (showStyle) => {
    return showStyle ? { borderLeft: "2px solid #27323b" } : {};
  };

  return userDetails.isPC() && userDetails.ac_subjects.length === 0 ? (
    <>
      <Heading as="h1" level="h2" border="bottom" margin="0 0 small 0">
        {t("setup.heading")}
      </Heading>
      <Alert variant="info">{t("dashboard.noAssociatedSubjects")}</Alert>
    </>
  ) : (
    <Grid>
      <Grid.Row>
        <Grid.Col>
          <Heading as="h1" level="h2" border="bottom">
            {t("setup.heading")}
          </Heading>
        </Grid.Col>
      </Grid.Row>
      <Grid.Row>
        <Grid.Col width="auto">
          <nav role="navigation" aria-label={t("setup.setupSubNav")}>
            <List isUnstyled>
              {userDetails.isAC() && (
                <List.Item>
                  <Heading as="h2" level="h4">
                    {t("setup.usersRoles")}
                  </Heading>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/users")}>
                    <SetupNavLink role="link" to="users">
                      {t("setup.users")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item itemSpacing={25}>
                  <Heading as="h2" level="h4">
                    {t("setup.dataInterpretationHeader")}
                  </Heading>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/status")}>
                    <SetupNavLink role="link" to="status">
                      {t("status.status")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <div
                    style={getNavStyle(
                      pathname === "/setup/taxonomy" || pathname === "/setup"
                    )}
                  >
                    <SetupNavLink role="link" to="taxonomy">
                      {t("setup.taxonomyLink")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/ccp")}>
                    <SetupNavLink role="link" to="ccp">
                      {t("setup.courseCodePatternLink")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <div
                    style={getNavStyle(pathname === "/setup/term-management")}
                  >
                    <SetupNavLink role="link" to="term-management">
                      {t("termManagement.termManagement")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              <List.Item>
                <Heading as="h2" level="h4">
                  {t("setup.institutionalOrg")}
                </Heading>
              </List.Item>
              <List.Item>
                <div style={getNavStyle(pathname === "/setup/program-types")}>
                  <SetupNavLink role="link" to="program-types">
                    {t("setup.programTypes")}
                  </SetupNavLink>
                </div>
              </List.Item>
              <List.Item>
                <div style={getNavStyle(pathname === "/setup/structure")}>
                  <SetupNavLink role="link" to="structure">
                    {t("setup.academicHierarchy")}
                  </SetupNavLink>
                </div>
              </List.Item>
              <List.Item>
                <Heading as="h2" level="h4">
                  {t("setup.curriculumMapHeader")}
                </Heading>
              </List.Item>
              {userDetails.isAC() && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/ilos")}>
                    <SetupNavLink role="link" to="ilos">
                      {t("setup.ilosLink")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              <List.Item>
                <div
                  style={getNavStyle(
                    pathname === "/setup/plos" ||
                      (pathname === "/setup" && userDetails.isPC())
                  )}
                >
                  <SetupNavLink role="link" to="plos">
                    {t("setup.plosLink")}
                  </SetupNavLink>
                </div>
              </List.Item>
              {userDetails.isAC() && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/attainment")}>
                    <SetupNavLink role="link" to="attainment">
                      {t("setup.attainmentLevelsLink")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              <List.Item>
                <Heading as="h2" level="h4">
                  {t("setup.assessments")}
                </Heading>
              </List.Item>
              <List.Item>
                <div style={getNavStyle(pathname === "/setup/mastery")}>
                  <SetupNavLink role="link" to="mastery">
                    {t("setup.masteryLevelsRubricsLink")}
                  </SetupNavLink>
                </div>
              </List.Item>
              <List.Item>
                <div style={getNavStyle(pathname === "/setup/cycles")}>
                  <SetupNavLink role="link" to="cycles">
                    {t("setup.cyclesLink")}
                  </SetupNavLink>
                </div>
              </List.Item>
              {(userDetails.isAC() || userDetails.isPC()) && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/assignment")}>
                    <SetupNavLink role="link" to="assignment">
                      {t("setup.assignmentTypeLink")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              {(userDetails.isAC() || userDetails.isPC()) && (
                <List.Item>
                  <div
                    style={
                      pathname === "/setup/templates"
                        ? { borderLeft: "2px solid #27323b" }
                        : { borderLeft: "2px solid white" }
                    }
                  >
                    <SetupNavLink role="link" to="templates">
                      {t("templates.planTemplates")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <Heading as="h2" level="h4">
                    {t("setup.resultsInterpretation")}
                  </Heading>
                </List.Item>
              )}
              {userDetails.isAC() && (
                <List.Item>
                  <div style={getNavStyle(pathname === "/setup/demographics")}>
                    <SetupNavLink role="link" to="demographics">
                      {t("setup.uploadDemographicsData")}
                    </SetupNavLink>
                  </div>
                </List.Item>
              )}
            </List>
          </nav>
        </Grid.Col>
        <Grid.Col>
          {/* Height is calculated by subtracting height and margin of the header (2.375rem & 1.5rem)
           and the padding around the page (3rem) from the height of the page (100vh) */}
          <View as="div" minHeight="calc(100vh - 2.375rem - 1.5rem - 3rem)">
            <Outlet />
          </View>
        </Grid.Col>
      </Grid.Row>
    </Grid>
  );
};

export default Setup;
